import { Close, Search } from "@mui/icons-material";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";

import Helpers from "../../../commons/helpers";
import { ICodename, IPagedList, IUser, PolicyRequirement } from "../../../commons/interfaces";
import { RoleHelpers } from "../../../commons/roleHelpers";
import { PolicyRequirementType } from "../../../constants/enums";
import useStrings from "../../../hooks/useStrings";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";
import UserService from "../../../services/identity/user.service";
import Avatar from "../../Avatar";
import CheckBox from "../../CheckBox";
import CustomPagination from "../../CustomPagination";
import FormField from "../../FormField";
import Modal from "../../Modal";
import { ItemType, avatarAndName, itemBox, typography } from "./styles";

interface IProps {
    visibled: boolean;
    requirements: PolicyRequirement[];
    roles: ICodename[];
    groups: ICodename[];
    serviceCode: string;
    onClose: () => void;
    onSubmit: (requirements: PolicyRequirement[]) => void;

    title?: string;
    showTabs: [PolicyRequirementType, PolicyRequirementType?, PolicyRequirementType?];
}

export const ModalAddRequirements: React.FC<IProps> = ({
    showTabs,
    title,
    visibled,
    serviceCode,
    requirements,
    roles,
    groups,
    onClose,
    onSubmit,
}: IProps) => {
    const Strings = useStrings();

    const { onError, clientId, tenantCode, serviceCode: curServiceCode, userInfo, language } = useCommonComponentContext();
    const [search, setSearch] = useState<string>("");
    const [data, setData] = useState<PolicyRequirement[]>(requirements);

    // const [roles, setRoles] = useState<ICodename[]>([]);
    // const [groups, setGroups] = useState<ICodename[]>([]);
    const [users, setUsers] = useState<ICodename[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = useState<number>(1);
    const [userLoading, setUserLoading] = useState<boolean>(true);

    const requirementTab = useMemo(() => {
        const tabs = [
            {
                code: PolicyRequirementType.Role,
                name: Strings.POLICY.ROLE,
            },
            {
                code: PolicyRequirementType.Group,
                name: Strings.POLICY.GROUP,
            },
            {
                code: PolicyRequirementType.User,
                name: Strings.POLICY.USER,
            },
        ];
        return tabs.filter((el) => showTabs.includes(el.code));
    }, [Strings, showTabs]);

    const [tabIndex, setTabIndex] = useState<PolicyRequirementType>(requirementTab[0]?.code);

    const [userGetPaged, setUserGetPaged] = useState<IPagedList<IUser>>({
        currentPage: 1,
        hasNext: false,
        hasPrevious: false,
        items: [],
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
    });

    const disabled = loading || userLoading;

    const dataByTab = useMemo(() => {
        return data.find((el) => el.type === tabIndex);
    }, [data, tabIndex]);

    useEffect(() => {
        const getData = async () => {
            setLoading(true);

            await getUsers({
                ids: [],
            });
            setLoading(false);
        };
        getData();
    }, []);

    const getUsers = async ({ ids, pageNumber, searchText }: { ids: string[]; pageNumber?: number; searchText?: string }) => {
        try {
            setUserLoading(true);

            if (RoleHelpers.isSuperAdmin(userInfo?.userProfile?.roleType, curServiceCode)) {
                const result = await UserService.getServiceUserPaged({
                    pageNumber: pageNumber || 1,
                    pageSize: 10,
                    listStatus: [1],
                    serviceCode: serviceCode,
                    selectedIds: ids,
                    searchText,
                });
                setUsers(
                    result.items.map((el) => ({
                        code: el.id,
                        name: Helpers.getFullName(el),
                        detail: {
                            avatarUrl: el.avatarUrl,
                        },
                    }))
                );
                setUserGetPaged(result);
                // setUsers(result.selectedItems || []);
            }
            if (!RoleHelpers.isSuperAdmin(userInfo?.userProfile?.roleType, curServiceCode)) {
                const result = await UserService.getPaged({
                    pageNumber: pageNumber || 1,
                    pageSize: 10,
                    listStatus: [1],
                    serviceCode: serviceCode,
                    selectedIds: ids,
                    searchText,
                });
                setUsers(
                    result.items.map((el) => ({
                        code: el.id,
                        name: Helpers.getFullName(el),
                        detail: {
                            avatarUrl: el.avatarUrl,
                        },
                    }))
                );
                setUserGetPaged(result);
                // setUsers(result.selectedItems || []);
            }
        } catch (error) {
            setUsers([]);
        } finally {
            setUserLoading(false);
        }
    };

    const onSearch = () => {};

    const handleSelectItem = (id: string) => {
        setData((prev) => {
            const temp = [...prev];
            const index = temp.findIndex((el) => el.type === tabIndex);
            const childIndex = (temp[index].values || []).findIndex((el) => el === id);
            if (childIndex === -1) temp[index].values = [...temp[index].values, id];
            if (childIndex !== -1) temp[index].values = temp[index].values.filter((el) => el !== id);
            return temp;
        });
    };

    const filteredData = useMemo(() => {
        if (tabIndex === PolicyRequirementType.Group && !Helpers.isNullOrEmpty(search))
            return groups.filter((item) => Helpers.getMultiContentLanguage(item.name).toLocaleLowerCase().includes(search.toLocaleLowerCase())) || [];
        if (tabIndex === PolicyRequirementType.Role && !Helpers.isNullOrEmpty(search))
            return roles.filter((item) => Helpers.getMultiContentLanguage(item.name).toLocaleLowerCase().includes(search.toLocaleLowerCase())) || [];
        // if (tabIndex === PolicyRequirementType.User && !Helpers.isNullOrEmpty(search))
        //     return users.filter((item) => Helpers.getMultiContentLanguage(item.name).toLocaleLowerCase().includes(search.toLocaleLowerCase())) || [];

        if (tabIndex === PolicyRequirementType.Group) return groups;
        if (tabIndex === PolicyRequirementType.Role) return roles;
        if (tabIndex === PolicyRequirementType.User) return users;
        return [];
    }, [search, tabIndex, roles, users, groups]);

    const handleSubmit = (requirements: PolicyRequirement[]) => {
        onSubmit(requirements);
        return onClose();
    };

    useEffect(() => {
        if (tabIndex === PolicyRequirementType.User && userLoading !== true) {
            setPage(1);
            getUsers({
                searchText: search,
                pageNumber: 1,
                ids: [],
            });
        }
    }, [search, tabIndex]);

    return (
        <Modal
            fullWidth
            maxWidth="lg"
            // hasActionButton
            // onAction={() => handleAction()}
            onClose={onClose}
            visible={visibled}
            title={title || Strings.POLICY.APPLICABLE_TARGETS}
            buttonAction={Strings.Common.SELECT}
        >
            <Box>
                <Box display="flex" alignItems="center" gap={1}>
                    {requirementTab.length > 1 && requirementTab.map((tab) => {
                        const isActive = tab.code === tabIndex;
                        return (
                            <Box
                                sx={({ palette: { info }, functions: { rgba } }) => ({
                                    width: 1,
                                    borderBottom: "2px solid transparent",
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "border-color .3s ease-in",
                                    ...(isActive && {
                                        borderBottomColor: info.main,
                                        p: {
                                            color: info.main,
                                        },
                                    }),

                                    cursor: "pointer",
                                })}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTabIndex(tab.code);
                                }}
                            >
                                <Typography variant="caption" fontWeight="bold">
                                    {tab.name}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
                <Box
                    sx={{
                        mt: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-end",
                    }}
                >
                    <FormField
                        disabled={disabled}
                        fullWidth
                        size="small"
                        defaultValue={search || ""}
                        variant="outlined"
                        placeholder={`${Strings.Common.SEARCH}...`}
                        onBlur={(value: any) => setSearch(value)}
                        onKeyPress={(e: any) => {
                            if (e.key === "Enter") {
                                setSearch(e.target.value);
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <IconButton color="info" onClick={() => onSearch()}>
                                    <Search />
                                </IconButton>
                            ),
                            endAdornment: search && (
                                <IconButton
                                    color="secondary"
                                    onClick={() => {
                                        setSearch(undefined);
                                    }}
                                >
                                    <Close />
                                </IconButton>
                            ),
                        }}
                    />
                </Box>
            </Box>

            <DataList
                {...{
                    data: filteredData,
                    handleSelectItem,
                    loading: tabIndex === PolicyRequirementType.User ? userLoading : loading,
                    requirement: dataByTab,
                }}
            />
            {tabIndex === PolicyRequirementType.User && (
                <Box mt={2}>
                    <CustomPagination
                        rowsPerPageOptions={[10]}
                        pageSize={10}
                        page={page}
                        total={userGetPaged?.totalCount}
                        onChangePage={(value) => {
                            setPage(value);
                            getUsers({ ids: [], pageNumber: value, searchText: search });
                        }}
                    />
                </Box>
            )}

            <Box gap={1} display="flex" justifyContent="flex-end" alignItems="flex-end" my={0.5}>
                <Button color="secondary" onClick={onClose}>
                    {Strings.Common.CANCEL}
                </Button>

                <Button onClick={() => handleSubmit(data)}>{Strings.Common.SAVE}</Button>
            </Box>
        </Modal>
    );
};

const DataList = ({
    loading,
    data,
    requirement,
    handleSelectItem,
}: {
    loading: boolean;
    data: ICodename[];
    requirement: PolicyRequirement;
    handleSelectItem: (id: string) => void;
}) => {
    const Strings = useStrings();
    const renderItem = useCallback(
        (item: ICodename) => {
            const isSelected = (requirement?.values || []).findIndex((el) => el === item.code) !== -1;
            return (
                <Box
                    key={item.code}
                    sx={(theme) => itemBox(theme, { isSelected, type: ItemType.User })}
                    onClick={() => handleSelectItem(item.code as string)}
                >
                    <Box>
                        <CheckBox
                            checked={isSelected}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectItem(item.code as string);
                            }}
                        />
                    </Box>
                    <Box sx={avatarAndName}>
                        <Avatar
                            src={item?.detail?.avatarUrl}
                            text={item?.name}
                            sizes="16"
                            sx={{
                                border: "1px #dddddd solid",
                            }}
                        />
                        <Box>
                            <Typography
                                sx={(theme: Theme) =>
                                    typography(theme, {
                                        fontSize: "0.8rem",
                                        fontWeight: "bold",
                                    })
                                }
                            >
                                {item.name}
                            </Typography>
                            {/* <Typography sx={typography}>{item.resourceURI}</Typography> */}
                        </Box>
                    </Box>
                    {/* <Box>
                        {isSelected ? (
                            <RadioButtonChecked color="info" sx={{ fontSize: "1rem" }} />
                        ) : (
                            <RadioButtonUnchecked sx={{ fontSize: "1rem" }} />
                        )}
                    </Box> */}
                </Box>
            );
        },
        [handleSelectItem, data]
    );

    return (
        <Box sx={{ height: 404, overflowY: "scroll" }}>
            {loading && (
                <Box height={1} display="flex" alignItems="center" justifyContent="center">
                    <CircularProgress />
                </Box>
            )}
            {!loading && data.length === 0 && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "70%",
                    }}
                >
                    <Typography variant="caption" fontWeight="bold">
                        {Strings.Common.NO_DATA}
                    </Typography>
                </Box>
            )}
            {!loading && data.length > 0 && (
                <Box
                    sx={{
                        mt: 1,
                        display: "grid",
                        gap: "0.5rem",
                        overflowY: "scroll",
                    }}
                >
                    {data.map(renderItem)}
                </Box>
            )}
        </Box>
    );
};

export default ModalAddRequirements;
